import React, { useCallback, useLayoutEffect, useRef, useState } from "react";

/**
 * Market / Limit.
 *
 * Was two 172px-wide underline tabs on a 48px rule — 344px of fixed width
 * inside a rail that is 340px at its narrowest, so the pair overflowed the
 * ticket on smaller desktops. Order type is a two-value choice made once per
 * order, so it takes as little room as a two-value choice should: a compact
 * pill switch that shares the ticket's header line with the market's name.
 */
const TABS = ["Market", "Limit"];

/* The selected pill, as a class for the same reason the direction switch uses
   one: in dark mode `--surface` on a `--surface-2` track is a two-point
   difference, which is not a selected state. The accent tint plus a hairline
   is the signal that survives on both grounds.

   It is a single thumb that slides between the tabs rather than a fill that
   appears under whichever tab was tapped — the same move the direction switch
   makes, so the ticket's two segmented controls behave alike. */
const TAB_CSS = `
  /* The tabs paint nothing — the thumb behind them is the fill. appearance
     is reset because a <button> with no author background falls back to the
     UA's buttonface, an opaque grey under color-scheme: dark, which would
     sit on top of the travelling thumb and hide it. */
  .ce-otab {
    -webkit-appearance: none;
    appearance: none;
    background-color: transparent !important;
    background-image: none;
  }
  /* The thumb is *raised*, not tinted-and-ringed. A sunken track holding one
     raised thumb is the same object as the header nav rail and the table's
     tabs, and the elevation says "selected" on both grounds without needing an
     accent ring to carry it. The accent moves to the label instead. */
  .ce-otab-thumb {
    position: absolute;
    top: 4px;
    bottom: 4px;
    border-radius: 999px;
    pointer-events: none;
    background-image: var(--raise);
    box-shadow: var(--pillow-sm);
    transition: transform 260ms var(--ease-spring), width 260ms var(--ease-spring);
  }
  /* Nothing to travel from on the first paint — the thumb would otherwise
     slide in from the left edge every time the ticket mounts. */
  .ce-otab-thumb.is-idle { transition: none; }
  @media (prefers-reduced-motion: reduce) {
    .ce-otab-thumb { transition: none; }
  }
`;

const TradeTabs = ({ tab, setTab }) => {
  const trackRef = useRef(null);
  const btnRefs = useRef([]);
  // "Market" and "Limit" are different widths, so the thumb cannot be derived
  // from the tab count the way the direction switch's equal halves can — it is
  // measured off the selected button and re-measured when the row resizes.
  const [seat, setSeat] = useState(null);
  const settled = useRef(false);

  const measure = useCallback(() => {
    const track = trackRef.current;
    const btn = btnRefs.current[tab];
    if (!track || !btn) return;
    const next = { x: btn.offsetLeft, w: btn.offsetWidth };
    setSeat((prev) => (prev && prev.x === next.x && prev.w === next.w ? prev : next));
  }, [tab]);

  useLayoutEffect(() => {
    measure();
    const track = trackRef.current;
    if (!track || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(track);
    return () => ro.disconnect();
  }, [measure]);

  // The first seat is placed, not animated; every one after it travels.
  const idle = !settled.current;
  if (seat && !settled.current) settled.current = true;

  return (
    <div
      ref={trackRef}
      role="tablist"
      aria-label="Order type"
      // `ce-raw` exempts the switch from the dark-mode sweep, as on the
      // direction switch — see the note there.
      className="ce-raw flex items-center"
      style={{
        position: "relative",
        gap: 2,
        padding: 4,
        borderRadius: 999,
        background: "var(--surface-2)",
        boxShadow: "var(--inset-sm)",
        border: 0,
      }}
    >
      <style>{TAB_CSS}</style>
      {seat && (
        <span
          aria-hidden="true"
          className={`ce-otab-thumb${idle ? " is-idle" : ""}`}
          style={{ left: 0, width: seat.w, transform: `translateX(${seat.x}px)` }}
        />
      )}
      {TABS.map((label, i) => {
        const active = tab === i;
        return (
          <button
            key={label}
            ref={(el) => { btnRefs.current[i] = el; }}
            className="ce-otab"
            role="tab"
            aria-selected={active}
            onClick={() => setTab(i)}
            style={{
              position: "relative",
              zIndex: 1,
              height: 30,
              padding: "0 16px",
              borderRadius: 999,
              border: "none",
              cursor: "pointer",
              fontFamily: "General Sans",
              fontWeight: active ? 600 : 500,
              fontSize: 13.5,
              color: active ? "var(--cat-accent, #5B1DEE)" : "var(--ink-3)",
              transition: "color .15s ease",
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
};

export default TradeTabs;
